import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../store";
import { fetchUserData } from "../store/features/userSlice";
import { resetPasswordApi, type ResetPasswordData } from "../api/usersApi";
import { useTranslation } from "react-i18next";
import type translations from "../translations/translations.json";
import { getErrorMessage } from "../utils/errorHandling";

type TranslationKeys = keyof typeof translations.en;

interface UseResetPasswordProps {
  userId: string;
}

export const useResetPassword = ({ userId }: UseResetPasswordProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const [formData, setFormData] = useState<ResetPasswordData>({
    _id: userId,
    password: "",
    newPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await resetPasswordApi(userId, formData);
      setSuccess(true);

      // Fetch updated user data
      await dispatch(fetchUserData());

      // Redirect to users page after 2 seconds
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (err) {
      setError(getErrorMessage(err, t, "resetPassword"));
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    loading,
    error,
    success,
    handleChange,
    handleSubmit,
  };
};
